// src/components/InvestmentDetails.tsx
import React from "react";

type Props = {
  amount?: number | string;          // 120000000 o "120.000.000"
  currency?: string;                 // "COP"
  accentHex?: string;                // color acento (default #E88809)
  onRequestInfo?: () => void;        // click del botón
};

const formatAmount = (value: number | string) => {
  if (typeof value === "string") return value;
  try {
    return new Intl.NumberFormat("es-CO").format(value);
  } catch {
    return String(value);
  }
};

const InvestmentDetails: React.FC<Props> = ({
  amount = 120_000_000,
  currency = "COP",
  accentHex = "#E88809",
  onRequestInfo,
}) => {
  const formatted = formatAmount(amount);

  return (
    <>
      <style>{`
        .investment-card {
          box-shadow: -0.3125rem 0 0 0 #F7AC48;
        }
        @media (min-width: 768px) {
          .investment-card {
            box-shadow: -0.625rem 0 0 0 #F7AC48;
          }
        }
      `}</style>
      <section
        aria-label="Detalles de inversión"
        className="w-full bg-white text-neutral-900 py-16 sm:py-20"
      >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Título */}
        <header className="text-center">
          <h2 className="font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>
            Inversión desde{" "}
            <span style={{ color: accentHex }}>
              ${formatted} {currency}
            </span>
          </h2>
        </header>

        {/* Contenido de 2 columnas */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
          {/* Card izquierda (oscura) */}
          <article className="rounded-xl bg-black text-white p-6 sm:p-7 md:p-8 shadow-lg hover:scale-105 hover:-translate-y-2 transition-all duration-300 ease-in-out cursor-pointer hover:shadow-xl">
            <div className="flex flex-col items-center justify-center gap-4 h-full text-center">
              {/* Sección: Ícono + Número + Subtítulo */}
              <div className="flex items-start gap-4">
                <img
                  src="/images/icons/money.svg"
                  alt="Ícono inversión"
                  className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
                />
                <div className="flex flex-col gap-1 text-left">
                  <div className="font-extrabold" style={{ color: accentHex, fontSize: 'clamp(1.8rem, 4vw, 2.7rem)' }}>
                    {formatted}
                  </div>
                  {/* Subtítulo alineado con el número */}
                  <div style={{ color: accentHex, fontSize: 'clamp(1rem, 2vw, 1.3rem)' }}>
                    Inversión inicial
                  </div>
                </div>
              </div>
              
              {/* Lista de beneficios */}
              <ul className="space-y-2 text-left" style={{ fontSize: 'clamp(1rem, 2vw, 1.3rem)' }}>
                <li className="list-disc list-inside">Incluye todo el modelo llave en mano</li>
                <li className="list-disc list-inside">Capacitación completa incluida</li>
                <li className="list-disc list-inside">Soporte continuo garantizado</li>
              </ul>
            </div>
          </article>

          {/* Card derecha (nota y CTA) */}
          <div className="flex flex-col gap-5">
            <article
              className="bg-white p-5 sm:p-6 md:p-7 investment-card"
              style={{
                borderRadius: '0.625rem',
                border: `0.0625rem solid #F7AC48`,
                background: '#FFF'
              }}
            >
              <div className="flex gap-3">
                <div className="pt-2">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: "black" }}
                    aria-hidden="true"
                  />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ color: accentHex, fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)' }}>
                    Este modelo no es para todos
                  </h3>
                  <p className="mt-1 text-neutral-700" style={{ fontSize: 'clamp(1rem, 2vw, 1.3rem)' }}>
                    Solo para quienes buscan construir un negocio real con respaldo,
                    tracción demostrada y una ruta clara hacia la rentabilidad.
                  </p>
                </div>
              </div>
            </article>

            <article className="rounded-xl bg-white p-5 sm:p-6 md:p-7 text-center border border-neutral-200">
              <h4 className="font-semibold text-neutral-900" style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)' }}>
                ¿Estás listo para abrir tu sede?
              </h4>
              <p className="mt-1 text-neutral-600" style={{ fontSize: 'clamp(1rem, 2vw, 1.3rem)' }}>
                Haz clic y llena el formulario. Te enviaremos toda la información,
                el dossier de franquicia y resolveremos tus dudas.
              </p>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={onRequestInfo}
                  className="inline-flex items-center justify-center rounded-md px-5 py-2.5 font-semibold text-white bg-black hover:opacity-90 focus:outline-none focus:ring-2"
                  style={{ 
                    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                    boxShadow: `0 0 0 2px rgba(0,0,0,0.0)`, 
                    borderColor: "transparent" 
                  }}
                >
                  Solicita información
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default InvestmentDetails;